import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The dialogs open right now, oldest first. Escape closes only the last one:
 * the App's own reason dialog opens on top of an edit dialog, and with every
 * open Modal listening on the document, one Escape closed both - the question
 * and the form the person was half way through.
 */
const openModals: object[] = [];

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return <Loader2 className={`${className} animate-spin`} aria-hidden />;
}

export function Loading({ text = 'Loading…' }: { text?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-ink-500 dark:text-ink-400">
      <Spinner />
      <span>{text}</span>
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="flex-1">
        <p>{message}</p>
        {onRetry && (
          <button type="button" onClick={onRetry} className="mt-2 font-medium underline">
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

export function Empty({ title, description, action }: {
  title: string; description?: string; action?: ReactNode;
}) {
  return (
    <div className="py-16 text-center">
      <p className="font-medium text-ink-700 dark:text-paper-300">{title}</p>
      {description && <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, width = 'max-w-2xl', fullOnMobile = false }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
  /**
   * Take the whole screen below the `sm` breakpoint. For reading surfaces: a
   * dialog that keeps its margins on a phone spends a quarter of a small
   * screen on chrome, and the draft preview is read on phones more than
   * anywhere else.
   */
  fullOnMobile?: boolean;
}) {
  // Escape closes the dialog on top, and the body cannot scroll behind it.
  // onClose is read through a ref: most callers pass a fresh arrow on every
  // render, and re-running the effect for it would move this dialog back to
  // the top of the stack over the one actually on top.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    if (!open) return;
    const self = {};
    openModals.push(self);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openModals[openModals.length - 1] === self) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      openModals.splice(openModals.indexOf(self), 1);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/50
                     ${fullOnMobile ? 'p-0 sm:p-6' : 'p-4 sm:p-6'}`}
    >
      <button
        type="button"
        className="fixed inset-0 -z-10 cursor-default"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`card w-full ${width} ${fullOnMobile
          ? 'my-0 min-h-dvh rounded-none border-0 sm:my-8 sm:min-h-0 sm:rounded-xl sm:border'
          : 'my-8'}`}
      >
        <div className="flex items-center justify-between border-b border-paper-200 px-4 py-3 dark:border-ink-700 sm:px-5 sm:py-4">
          <h2 className="min-w-0 truncate text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-ink-400 hover:bg-paper-200 hover:text-ink-600
                       dark:hover:bg-ink-700 dark:hover:text-paper-300"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className={fullOnMobile ? 'px-2 py-2 sm:px-5 sm:py-4' : 'px-5 py-4'}>{children}</div>
      </div>
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  live: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
  in_development: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  paused: 'bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300',
  retired: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300',
};

export function StatusBadge({ status, label }: { status: string; label: string }) {
  return (
    <span className={`badge ${STATUS_STYLES[status] ?? STATUS_STYLES.paused}`}>{label}</span>
  );
}

/**
 * Live indicator from the most recent automated uptime check. A check older
 * than an hour is stale (the cron runs every 15 minutes), so it shows as
 * unknown rather than pretending yesterday's answer is today's.
 */
export function LiveDot({ check }: {
  check: { ok: number; checked_at: string } | null | undefined;
}) {
  if (!check) return null;
  const stale = Date.now() - new Date(check.checked_at).getTime() > 60 * 60_000;
  const state = stale ? 'unknown' : check.ok ? 'up' : 'down';
  const styles = {
    up: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400', label: 'Up' },
    down: { dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400', label: 'Down' },
    unknown: { dot: 'bg-paper-400 dark:bg-ink-500', text: 'text-ink-500 dark:text-ink-400', label: 'Not checked recently' },
  }[state];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${styles.text}`}>
      <span className={`h-2 w-2 rounded-full ${styles.dot}`} aria-hidden />
      {styles.label}
    </span>
  );
}
